import { useNavigate, useParams } from 'react-router';
import { TableColumnsType } from 'antd';
import { useQuery } from '@apollo/client';

import { useLocationName } from '../../../../hooks/useLocationName';

import { GET_DASHBOARD } from './queries';

import {
  Dashboard,
  ProcessAreasAndProcessesWorkaroundsDashboard,
  WorkaroundType,
} from './types';

import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import FinancialData from '../../../../components/common/FinancialData';
import { Button, Footer, TitleBar } from '../../../../components';
import TableV3 from '../../../../components/generic/Table/TableV3';
import { useState } from 'react';
import { CompletionPercentage } from '../../components';

const ProcessAreaAndProcessesWorkarounds = () => {
  const { locationUUID } = useParams();
  const { locationName } = useLocationName(locationUUID);
  const [dashboard, setDashboard] = useState<Dashboard[]>([]);
  const navigate = useNavigate();

  const { loading, error } =
    useQuery<ProcessAreasAndProcessesWorkaroundsDashboard>(GET_DASHBOARD, {
      variables: {
        locationUUID,
      },
      onCompleted: (data) => {
        setDashboard(data.getProcessAreaWorkaroundsDashboard);
      },
    });

  const columns: TableColumnsType<Dashboard> = [
    {
      title: '',
      width: 300,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Annual Revenue <br />
          Supported
        </span>
      ),
      width: 142,
      dataIndex: 'revenue',
      key: 'revenue',
      fixed: 'left',
      align: 'center',
      render: (text: number) => (
        <span>{<FinancialData text={text} formatType="decimal" />}</span>
      ),
    },
    {
      title: '# of Applications Tagged',
      width: 105,
      dataIndex: 'applicationsTagged',
      key: 'applicationsTagged',
      align: 'center',
      render: (text: number) => <span>{text}</span>,
    },
    {
      title: 'Workaround Completion',
      width: 105,
      dataIndex: 'completionPercentage',
      key: 'completionPercentage',
      align: 'center',
      render: (completionPercentage: number) => (
        <CompletionPercentage percent={completionPercentage} />
      ),
    },
  ];

  const extendActions = (record: {
    uuid: string;
    type: WorkaroundType;
    parentUUID: string;
  }) => {
    const { uuid, type, parentUUID } = record;
    const path = `/cyber/location-level/${locationUUID}/process-areas-and-processes/workarounds/${
      type !== WorkaroundType.PROCESS ? `${uuid}` : `${parentUUID}/${uuid}`
    }`;
    return (
      <div className="relative w-full h-7">
        <Button
          type="primary-rounded"
          className="w-full text-sm font-semibold whitespace-nowrap min-w-max"
          onClick={() => navigate(path)}
        >
          Add Workarounds
        </Button>
      </div>
    );
  };

  const backPath = `/cyber/location-level/${locationUUID}/application-workarounds`;

  return (
    <div>
      <TitleUpdater title="Process Area & Process Workarounds" />
      <TitleBar
        title="Process Area & Process Workarounds"
        subtitle={`${locationName} Process Areas Overview`}
        brief="In the matrix, check the respective process areas or processes supported by each application."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={dashboard}
          loading={loading}
          scroll={{ x: 800, y: 540 }}
          tableSize="small"
          extendActions={extendActions}
        />
      </QueryErrorBoundary>
      <Footer backPath={backPath} />
    </div>
  );
};

export default ProcessAreaAndProcessesWorkarounds;
